import { useEffect, useMemo } from "react";
import { useMutation } from "@tanstack/react-query";
import { useCustomerId } from "@/components/customer-id-provider";
import { Item } from "../components/shopping-cart-provider";
import { useResponsive } from "./useIsMobile";
import { useDebounce } from "./use-debounce";
import { syncCartMutation } from "@/client/@tanstack/react-query.gen";

const DEBOUNCE_MS = 1500; // 1.5 seconds

const getBrowserName = (): string => {
  if (typeof navigator === "undefined" || !navigator.userAgent) {
    return "Server-Side";
  }

  const userAgent = navigator.userAgent;

  if (userAgent.includes("Edg")) {
    return "Edge";
  }
  if (userAgent.includes("Chrome")) {
    return "Chrome";
  }
  if (userAgent.includes("Firefox")) {
    return "Firefox";
  }
  if (userAgent.includes("Safari")) {
    return "Safari";
  }
  if (userAgent.includes("Trident") || userAgent.includes("MSIE")) {
    return "IE";
  }

  return "Unknown";
};

/**
 * Custom Hook for debounced cart synchronization with backend.
 * Fire-and-forget approach - no error handling propagates to UI.
 */
export const useCartSync = (items: Item[], cartSessionId?: string) => {
  const { customerId } = useCustomerId();
  const { isMobile, isTablet } = useResponsive();
  const deviceType = isMobile ? "mobile" : isTablet ? "tablet" : "desktop";

  // Memoize payload to avoid unnecessary re-renders
  const payload = useMemo(() => {
    if (!customerId) {
      return null;
    }

    // Don't sync if we don't have a session ID
    // The CartProvider is responsible for generating session IDs
    // This is expected during initialization or after cart is emptied
    if (!cartSessionId) {
      return null;
    }

    return {
      items: items.map((item) => ({
        machineId: item.machineId,
        asMiId: item.asMiId,
        quantity: item.quantity ?? 1,
        level1AncestorItemId: item.level1AncestorItemId || undefined,
        level1AncestorItemDrawingNo: item.level1AncestorItemDrawingNo || undefined,
      })),
      deviceType: deviceType as "mobile" | "tablet" | "desktop" | undefined,
      browserSession: getBrowserName(),
      cartSessionId: cartSessionId,
    };
  }, [customerId, items, deviceType, cartSessionId]);

  // Debounce the payload
  const debouncedPayload = useDebounce(payload, DEBOUNCE_MS);

  // Mutation for cart sync (fire-and-forget)
  const syncMutation = useMutation({
    ...syncCartMutation(),
    retry: false, // Don't retry for fire-and-forget tracking
    throwOnError: false, // Don't throw errors - silently handle them
    onError: (error) => {
      // Only log errors silently, don't propagate to UI or display to users
      // Backend errors (including 500) are logged but never shown to users
      console.error("Cart sync failed (silently):", error);
    },
  });

  // Sync when debounced payload changes
  useEffect(() => {
    if (debouncedPayload && customerId) {
      syncMutation.mutate({
        path: { customer_id: customerId },
        body: debouncedPayload,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedPayload, customerId]);

  return null;
};

export default useCartSync;

